
(() => {
  const config = window.vendorOrderFeeConfig;
  if (!config || window.vendorOrderFeesInstalled) return;
  window.vendorOrderFeesInstalled = true;
  const normalise = value => String(value || '').trim().toUpperCase();
  const cents = value => /^\d+(?:\.\d{1,2})?$/.test(String(value || '').trim())
    ? Math.round(Number(value) * 100) : NaN;
  const rules = (config.rules || []).map(rule => ({ ...rule,
    vendor: normalise(rule.vendor), feeVariantId: String(rule.feeVariantId || ''),
    minimumCents: cents(rule.minimum), feeCents: cents(rule.amount)
  }));
  const activeRules = rules.filter(rule => rule.enabled);
  const feeIds = new Set(rules.map(rule => rule.feeVariantId).filter(Boolean));
  if (!activeRules.length && !feeIds.size) return;
  document.documentElement.classList.toggle('assa-fee-active', activeRules.length > 0);
  function validateRules() {
    const vendors = new Set(), variants = new Set();
    for (const rule of rules) {
      if (rule.feeVariantId) {
        if (variants.has(rule.feeVariantId)) throw new Error('Select a different fee product for each vendor rule.');
        variants.add(rule.feeVariantId);
      }
      if (!rule.enabled) continue;
      if (!rule.vendor || !/^\d+$/.test(rule.feeVariantId) ||
          !Number.isSafeInteger(rule.minimumCents) || rule.minimumCents <= 0 ||
          !Number.isSafeInteger(rule.feeCents) || rule.feeCents <= 0) {
        throw new Error('An enabled vendor rule needs a vendor, positive amounts and a fee product.');
      }
      if (vendors.has(rule.vendor)) throw new Error('Use one enabled rule per vendor.');
      vendors.add(rule.vendor);
    }
  }
  const root = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  let externalRequests = 0, timer, running, dirty = false, editReadyAt = 0;
  const allowedForms = new WeakSet();
  function sectionIds() {
    // Request only the cart surfaces actually present on this theme page.
    return [...new Set([
      document.querySelector('cart-drawer') && 'cart-drawer',
      document.getElementById('cart-icon-bubble') && 'cart-icon-bubble',
      document.getElementById('cart-live-region-text') && 'cart-live-region-text',
      document.getElementById('main-cart-items')?.dataset.id
    ].filter(Boolean))];
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await originalFetch(url, { ...options, signal: controller.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Cart request failed.');
      return result;
    } finally { clearTimeout(timeout); }
  }

  async function api(path, body) {
    return request(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    } : { cache: 'no-store' });
  }

  function plan(cart) {
    const fees = cart.items.filter(item => feeIds.has(String(item.variant_id)));
    const states = rules.map(rule => {
      const merchandise = cart.items.filter(item =>
        !feeIds.has(String(item.variant_id)) && normalise(item.vendor) === rule.vendor);
      const subtotal = merchandise.reduce((total, item) => total + item.final_line_price, 0);
      const required = rule.enabled && merchandise.length > 0 && subtotal < rule.minimumCents;
      const keep = required ? fees.find(item => String(item.variant_id) === rule.feeVariantId) : null;
      return { rule, subtotal, required, keep, hasMerchandise: merchandise.length > 0 };
    });
    const kept = new Set(states.map(state => state.keep).filter(Boolean));
    const updates = {};
    for (const fee of fees) if (!kept.has(fee)) updates[fee.key] = 0;
    for (const state of states) if (state.keep && state.keep.quantity !== 1) updates[state.keep.key] = 1;
    return { states, updates, missing: states.filter(state => state.required && !state.keep) };

  }

  function render(cart) {
    const active = document.activeElement;
    const inputVariant = active?.dataset?.quantityVariantId;
    const pageId = document.getElementById('main-cart-items')?.dataset.id;
    const fragments = [
      ['cart-drawer', '.cart-drawer__items', '.cart-drawer__items'],
      ['cart-drawer', '.cart-drawer__footer--subtotal', '.cart-drawer__footer--subtotal'],
      ['cart-icon-bubble', '#cart-icon-bubble', '.shopify-section'],
      ['cart-live-region-text', '#cart-live-region-text', '.shopify-section'],
      [pageId, '#main-cart-items .js-contents', '.js-contents'],
      [pageId, '#main-cart-footer .cart__footer__js-contents', '.cart__footer__js-contents']
    ];
    const parsed = {};
    for (const [id, targetSelector, sourceSelector] of fragments) {
      if (!id || !cart.sections?.[id]) continue;
      parsed[id] ||= new DOMParser().parseFromString(cart.sections[id], 'text/html');
      const target = document.querySelector(targetSelector);
      const replacement = parsed[id].querySelector(sourceSelector);
      if (target && replacement) target.innerHTML = replacement.innerHTML;
    }
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart')?.classList.toggle('is-empty', cart.item_count === 0);
    decorate(cart);
    if (inputVariant) {
      [...document.querySelectorAll('[data-quantity-variant-id]')]
        .find(input => input.dataset.quantityVariantId === inputVariant && input.offsetParent)?.focus();
    }
    // Use Woodstock's recommendation hooks without opening/closing the drawer.
    document.querySelector('cart-drawer')?.updateProductRecommendation?.();
    document.querySelector('main-cart-recommendations')?.update?.();
    document.querySelectorAll('free-shipping-bar').forEach(bar => bar.onCartChange?.());
  }

  function decorate(cart) {
    const state = plan(cart);
    const currency = cart.currency || config.currency;
    if (currency !== config.currency) throw new Error('This fee rule supports the store currency only.');
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en',
      { style: 'currency', currency }).format(cents / 100);
    document.querySelectorAll('[data-assa-fee-notice]').forEach(node => node.remove());
    for (const entry of state.states.filter(entry => entry.rule.enabled && entry.hasMerchandise)) {
      document.querySelectorAll('.cart-drawer__footer--subtotal, #main-cart-footer .cart__footer__js-contents')
        .forEach(target => {
          const notice = document.createElement('p');
          notice.dataset.assaFeeNotice = '';
          notice.className = 'assa-fee-notice';
          notice.setAttribute('role', 'status');
          notice.textContent = `${entry.rule.vendor} subtotal: ${money(entry.subtotal)}. ` +
            (entry.required ? `Small order fee: ${money(entry.rule.feeCents)} for orders below ${money(entry.rule.minimumCents)}.`
              : 'No small order fee applies.');
          target.appendChild(notice);
        });
    }
    document.querySelectorAll('[data-quantity-variant-id], input[data-line-key]').forEach(input => {
      const line = cart.items[Number(input.dataset.index) - 1];
      if (line) input.dataset.lineKey = line.key;
      const variantId = input.dataset.quantityVariantId || input.dataset.lineKey?.split(':')[0];
      if (!feeIds.has(String(variantId))) return;
      const row = input.closest('.cart-item');
      if (!row) return;
      input.readOnly = true;
      input.setAttribute('aria-label', 'Required small order fee quantity');
      row.querySelectorAll('quantity-input button, cart-remove-button').forEach(button => {
        button.style.display = 'none';
      });
    });
  }

  async function refresh(cart) {
    const url = new URL(location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    const sections = await request(url.href, { cache: 'no-store' });
    if (Object.values(sections).some(html => html === null)) throw new Error('A cart section failed to render.');
    render({ ...cart, sections });
  }

  function showError(error) {
    console.error('Vendor order fees:', error);
    let notice = document.getElementById('assa-fee-error');
    if (!notice) {
      notice = document.createElement('p');
      notice.id = 'assa-fee-error';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:16px;left:16px;right:16px;padding:16px;background:white;color:#b00020;border:1px solid currentColor;z-index:9999';
      document.body.appendChild(notice);
    }
    notice.textContent = config.designMode ? error.message : 'We could not verify the vendor order fees. Please refresh your cart and try again.';
  }

  async function synchronise() {
    validateRules();
    const deadline = Date.now() + 30000;
    let sections;
    for (let corrections = 0; corrections < 8 && Date.now() < deadline;) {
      if (externalRequests || Date.now() < editReadyAt ||
          document.querySelector('product-form .loading, .cart__items--disabled')) {
        await new Promise(resolve => setTimeout(resolve, 100)); continue;
      }
      dirty = false;
      const cart = await api('cart.js');
      if (externalRequests || dirty) { sections = null; continue; }
      if ((cart.currency || config.currency) !== config.currency) throw new Error('Unsupported cart currency.');
      const state = plan(cart);
      const sectionRequest = { sections: sectionIds(), sections_url: location.pathname };
      if (Object.keys(state.updates).length) {
        // Repair all existing fee lines in one request; never update merchandise quantities.
        const updated = await api('cart/update.js', { updates: state.updates, ...sectionRequest });
        sections = updated.sections;
      } else if (state.missing.length) {
        // Add all required vendors' fee products together, once each.
        const added = await api('cart/add.js', {
          items: state.missing.map(entry => ({ id: entry.rule.feeVariantId, quantity: 1 })),
          ...sectionRequest
        });
        sections = added.sections;
      } else {
        for (const entry of state.states) {
          if (entry.keep && entry.keep.final_line_price !== entry.rule.feeCents) {
            throw new Error(`${entry.rule.vendor}: fee product price must match the configured fee without discounts.`);
          }
        }
        if (sections && sectionIds().every(id => typeof sections[id] === 'string')) {
          render({ ...cart, sections });
        } else {
          await refresh(cart);
        }
        if (externalRequests || dirty) { sections = null; continue; }
        document.getElementById('assa-fee-error')?.remove();
        return;
      }
      if (externalRequests || dirty) sections = null;
      corrections++;
    }
    throw new Error('The cart kept changing. Please try again.');
  }

  function ensure() {
    clearTimeout(timer);
    if (running) return running;
    document.documentElement.classList.add('assa-fee-syncing');
    running = synchronise().finally(() => {
      running = null;
      document.documentElement.classList.remove('assa-fee-syncing');
    });
    return running;
  }

  function schedule() {
    dirty = true;
    clearTimeout(timer);
    timer = setTimeout(() => ensure().catch(showError), 200);
  }

  // Observe Woodstock fetch-based cart mutations without changing their requests/responses.
  window.fetch = async (...args) => {
    const input = args[0];
    let url;
    try { url = new URL(typeof input === 'string' ? input : input.url || String(input), location.href); }
    catch (_) { return originalFetch(...args); }
    const mutation = url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    if (!mutation) return originalFetch(...args);
    externalRequests++; dirty = true;
    try { return await originalFetch(...args); }
    finally { externalRequests--; schedule(); }
  };

  let checkingCheckout = false;
  async function checkout(formId, url) {
    if (checkingCheckout) return;
    checkingCheckout = true;
    document.documentElement.classList.add('assa-fee-checking');
    try {
      // Allow Woodstock's 300ms debounced quantity edits to finish starting.
      await new Promise(resolve => setTimeout(resolve, 400));
      await ensure();
      if (url) { location.assign(url); return; }
      const form = document.getElementById(formId);
      if (!form || !form.checkValidity()) { form?.reportValidity(); return; }
      const button = form.querySelector('[name="checkout"]') ||
        [...document.querySelectorAll('button[name="checkout"]')].find(node => node.form === form);
      if (!button) throw new Error('Checkout button not found.');
      allowedForms.add(form);
      form.requestSubmit(button);
    } catch (error) { showError(error); }
    finally { checkingCheckout = false; document.documentElement.classList.remove('assa-fee-checking'); }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (button?.form) {
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(button.form.id); return;
    }
    const link = event.target.closest?.('a[href]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(null, url.href);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (allowedForms.has(form)) { allowedForms.delete(form); return; }
    if (event.submitter?.name !== 'checkout' && form.id !== 'cart-notification-form') return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(form.id);
  }, true);

  ['input', 'change'].forEach(name => document.addEventListener(name, event => {
    if (!event.target.matches?.('input[data-quantity-variant-id], input[name="updates[]"]')) return;
    editReadyAt = Date.now() + 400;
    schedule();
  }, true));

  // Woodstock emits these after its cart DOM updates.
  ['afterCartChanged', 'afterUpdateQuantity', 'afterDiscountUpdate']
    .forEach(name => document.addEventListener(name, schedule));
  // Quick-add may render after its drawer-close animation. Reconcile that late render too.
  ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'].forEach(name => {
    customElements.whenDefined(name).then(() => {
      const proto = customElements.get(name).prototype;
      if (!Object.prototype.hasOwnProperty.call(proto, 'renderContents')) return;
      const original = proto.renderContents;
      proto.renderContents = function(...args) {
        const result = original.apply(this, args);
        schedule();
        return result;
      };
    });
  });

  window.addEventListener('pageshow', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();
